// import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from '@firebase/firestore'
import { db } from 'src/firebase/config'
import { toast } from 'react-toastify'
import { showLoader } from 'src/utils/loader'

const useFireStore = () => {
  const navigate = useNavigate()
  // const dispatch = useDispatch()

  /**
   * @param {string} table
   * @returns
   */
  const getSnapshot = (table) => {
    if (!table) {
      toast.error('Table is required')
      return
    }

    const unSub = onSnapshot(query(collection(db, table)), (querySnapshot) => {
      var items = []
      querySnapshot.forEach((snapDoc) => {
        if (snapDoc.exists()) {
          items.push({
            ...snapDoc.data(),
            id: snapDoc.id,
          })
        }
      })
    })
    return unSub
  }

  /**
   * @param {string} table
   * @param {string} id
   * @returns
   */
  const getDocument = (table, id) => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!id) {
      toast.error('id is required')
      return
    }
    return new Promise((resolve, reject) => {
      getDoc(doc(collection(db, table), id))
        .then((docSnap) => resolve({ id: docSnap.id, ...docSnap.data() }))
        .catch((error) => {
          reject(error)
        })
    })
  }

  /**
   * @param {string} table
   * @param {*} condition
   * @returns
   */
  const getDocuments = (table, condition = [], loader = true) => {
    // const getDocuments = (table, condition = [], between = [], loader = true) => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    return new Promise((resolve, reject) => {
      loader && showLoader(true)
      const tableRef = collection(db, table)
      // let docRef = getDocs(collection(db, table))
      let queryConstraints = []
      // if (condition.length >= 2) {
      //   // docRef = getDocs(query(collection(db, table), where(condition[0], condition[1], condition[2])))
      //   queryConstraints.push(where(condition[0], condition[1], condition[2]))
      // }
      if (condition.length >= 1) {
        condition.forEach((w) => {
          queryConstraints.push(where(w.field, w.op, w.value))
        })
      }
      // if (between.length >= 2) {
      //   between[1] && queryConstraints.push(where(between[0], '<=', between[1]))
      //   between[2] && queryConstraints.push(where(between[0], '>=', between[2]))
      // }

      getDocs(query(tableRef, ...queryConstraints))
        .then((querySnapshot) => {
          var items = []
          querySnapshot.forEach((docSnap) => {
            if (docSnap.exists()) {
              items.push({
                ...docSnap.data(),
                id: docSnap.id,
              })
            }
            return resolve(items)
          })
          // .then(snapshot => {
          //   loader && showLoader(false)
          //   return snapshot
          // })

          // dispatch({
          //   type: table,
          //   items: items,
          //   status: "idle"
          // })
        })
        .catch((error) => {
          console.log(error)
          return reject(error)
          // return error
        })
        .finally(() => {
          loader && showLoader(false)
        })
    })
  }

  /**
   * @param {string} table
   * @param {string} searchBy
   * @param {string} searchValue
   * @param {*} stickyCondition
   * @param {boolean} loader
   * @returns
   */
  const searchDocuments = (table, searchBy, searchValue, stickyCondition = [], loader = true) => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!searchBy) {
      toast.error('searchBy is required')
      return
    }
    if (!searchValue) {
      toast.error('searchValue is required')
      return
    }
    return new Promise((resolve, reject) => {
      let queryConstraints = []
      loader && showLoader(true)
      // if (stickyCondition.length >= 2) {
      //   queryConstraints.push(where(stickyCondition[0], stickyCondition[1], stickyCondition[2]))
      // }
      if (stickyCondition.length >= 1) {
        stickyCondition.forEach((w) => {
          queryConstraints.push(where(w.field, w.op, w.value))
        })
      }
      // queryConstraints.push(where(searchBy, '>=', String(searchValue).toUpperCase()))
      // queryConstraints.push(where(searchBy, '<=', String(searchValue).toLowerCase() + '\uf8ff'))
      queryConstraints.push(where(searchBy, '>=', searchValue))
      queryConstraints.push(where(searchBy, '<=', searchValue + '\uf8ff'))

      // console.log('queryConstraints', queryConstraints)
      getDocs(query(collection(db, table), ...queryConstraints))
        .then((snapshot) => {
          loader && showLoader(false)
          var items = []
          snapshot.forEach((doc, i) => {
            if (doc.exists()) {
              items.push({
                ...doc.data(),
                id: doc.id,
              })
            }
          })
          return resolve(items)
        })
        .catch((error) => {
          console.log(error)
          return reject(error)
        })
        .finally(() => {
          loader && showLoader(false)
        })
    })
  }

  /**
   * @param {string} table
   * @param {*} payload
   * @param {string} message
   * @param {string} redirectTo
   */
  const addDocument = async (table, payload, message = '', redirectTo = '') => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!payload) {
      toast.error('payload is required')
      return
    }
    showLoader(true)
    payload.createdAt = serverTimestamp()
    return await addDoc(collection(db, table), payload)
      .then((docSnap) => {
        message && toast.success(message)
        redirectTo && navigate(redirectTo)
        return docSnap
      })
      .catch((error) => {
        var errorMessage = error.message
        console.log('error', error)
        toast.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  }

  /**
   * @param {string} table
   * @param {*} payload
   * @param {string} message
   * @param {string} redirectTo
   */
  const setDocument = (table, payload, docId, message = '', redirectTo = '') => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!payload) {
      toast.error('payload is required')
      return
    }
    showLoader(true)
    payload.createdAt = serverTimestamp()
    const docRef = doc(db, table, docId)
    setDoc(docRef, payload)
      .then((docSnap) => {
        message && toast.success(message)
        redirectTo && navigate(redirectTo)
      })
      .catch((error) => {
        var errorMessage = error.message
        console.log('error', error)
        toast.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  }

  /**
   * @param {string} table
   * @param {*} payload
   * @param {string} id
   * @param {string} message
   * @param {string} redirectTo
   */
  const updateDocument = async (table, payload, id, message = '', redirectTo = '') => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!id) {
      toast.error('id is required')
      return
    }
    if (!payload) {
      toast.error('payload is required')
      return
    }
    showLoader(true)
    payload.updatedAt = serverTimestamp()
    return await updateDoc(doc(db, table, id), payload)
      .then((docSnap) => {
        message && toast.success(message)
        redirectTo && navigate(redirectTo)
      })
      .catch((error) => {
        var errorMessage = error.message
        console.log('error', error)
        toast.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  }

  /**
   * @param {string} table
   * @param {string} fieldName
   * @param {*} fieldValue
   * @param {string} id
   * @param {string} message
   */
  const updateDocumentField = (table, field, value, id, message) => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!id) {
      toast.error('id is required')
      return
    }
    return new Promise((resolve, reject) => {
      showLoader(true)
      updateDoc(doc(db, table, id), field, value)
        .then((docSnap) => {
          message && toast.success(message)
          resolve(true)
        })
        .catch((error) => {
          console.log('error', error)
          reject(error)
        })
        .finally(() => {
          showLoader(false)
        })
    })
  }

  /**
   * @param {string} table
   * @param {string} id
   * @param {string} message
   */
  const deleteDocument = (table, id, message = '') => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!id) {
      toast.error('id is required')
      return
    }
    return new Promise((resolve) => {
      if (!id) {
        toast.error('id not found')
        return
      }
      showLoader(true)
      deleteDoc(doc(db, table, id))
        .then(() => {
          message && toast.success(message)
          return resolve(id)
        })
        .catch((error) => {
          // var errorCode = error.code;
          var errorMessage = error.message
          toast.error(errorMessage)
        })
        .finally(() => {
          showLoader(false)
        })
    })
  }

  /**
   *
   * @param {string} table
   * @param {string} id
   * @param {string} message
   */
  const deleteDocumentField = (table, id, fieldName, message = '') => {
    if (!table) {
      toast.error('Table is required')
      return
    }
    if (!id) {
      toast.error('id is required')
      return
    }
    return new Promise((resolve) => {
      showLoader(true)
      const docRef = doc(db, table, id)
      updateDoc(docRef, {
        [fieldName]: deleteField(),
      })
        .then(() => {
          message && toast.success(message)
          return resolve(id)
        })
        .catch((error) => {
          // var errorCode = error.code;
          var errorMessage = error.message
          toast.error(errorMessage)
        })
        .finally(() => {
          showLoader(false)
        })
    })
  }

  const makeTimestamp = (value, useOffset = true) => {
    if (value) {
      // var Timestamp = firebase.firestore.Timestamp
      // return firebase.firestore.Timestamp.fromDate(new Date)
      // console.log(firebase.firestore.Timestamp.fromDate(new Date(value).toISOString()))
      // console.log(value.valueAsNumber)
      // return firebase.firestore.Timestamp.fromDate(new Date(value).toISOString())
      // var seconds = Math.floor(new Date(value).valueOf() / 1000)
      // return new Timestamp( seconds, 0)
      // var Timestamp = firebase.firestore.Timestamp
      // console.log(firebase.firestore.Timestamp.fromDate(new Date(value).getUTCSeconds()))
      // // return firebase.firestore.Timestamp.fromDate(value)
      // var seconds = ''
      // if (useOffset) {
      //   seconds = Math.floor(new Date(value).valueOf() / 1000 + (new Date(value).getTimezoneOffset() * 60))
      // } else {
      //   seconds = Math.floor(new Date(value).valueOf() / 1000)
      // }
      // return new Timestamp( seconds, 0)

      // return firebase.firestore.Timestamp.fromDate(moment(value).utc().format())
      // return Timestamp.fromDate(value)
      // console.log(moment(value))
      // console.log('fromMillis moment', Timestamp.fromMillis(moment(value)))
      // console.log('fromMillis', Timestamp.fromMillis(value))
      // console.log('fromDate', Timestamp.fromDate(value))
      return Timestamp.fromMillis(moment(value))
      // return Timestamp.fromDate(value)
      // return firebase.firestore.Timestamp.fromMillis(new Date(value).valueOf())
      // var seconds2 = Math.floor(new Date(value).valueOf() / 1000 - (new Date(value).getTimezoneOffset() * 60))
      // console.log('seconds', seconds)
      // console.log('seconds2', seconds2)
      // var seconds = Math.floor(new Date(value).valueOf() / 1000)
      // var seconds = moment(seconds).utc().unix()
      // return firebase.firestore.Timestamp.fromDate(value)
      // return new Timestamp( value.valueAsNumber, 0)
    }
    return ''
  }

  return {
    getSnapshot,
    getDocument,
    getDocuments,
    searchDocuments,
    addDocument,
    setDocument,
    updateDocument,
    updateDocumentField,
    deleteDocument,
    deleteDocumentField,
    makeTimestamp,
  }
}

export { useFireStore }
